import React, { useCallback, useEffect, useState } from 'react';
import {
  BillingPolicyReturn,
  HomeReturn,
  PaymentMethod,
  PaymentReturn,
  PaymentStatus,
} from '../clients/rust-api-client';
import { billingService, homeService } from '../clients/ApiClients';
import { Button } from '../common/Catalyst/button';
import { Divider } from '../common/Catalyst/divider';
import { Input } from '../common/Catalyst/input';
import { Subheading } from '../common/Catalyst/heading';
import { Text } from '../common/Catalyst/text';
import { FieldPicker } from '../common/Custom/FieldPicker';
import LoadingSpinner from '../LoadingSpinner';
import ShowAlert from '../ShowAlert';
import {
  billingErrorMessage,
  sumMoney,
  formatMoney,
} from '../utils/billingHelpers';
import { paymentMethod, paymentStatus } from './enumLabels';
import PaymentCard from './PaymentCard';
import RecordPaymentDialog from './RecordPaymentDialog';
import {
  MatchPaymentDialog,
  AssessFeeDialog,
  RefundPaymentDialog,
  ReturnPaymentDialog,
} from './PaymentActionDialogs';

interface Props {
  hoaId: string;
  canRecord: boolean;
  canRefund: boolean;
  canReturn: boolean;
}

const PER_PAGE = 25;
/**
 * The filter pickers' "Any …" value. FieldPicker reads '' as nothing
 * chosen, so the no-filter choice gets its own value and maps back to ''.
 */
const ANY = '__any__';

/**
 * What came in, and what still needs a decision.
 *
 * The suspense queue is deliberately above the list rather than being a
 * filter on it: money we cannot attribute is the one thing on this page
 * that is actively waiting for a person, and burying it behind a filter
 * is how it sits there for a month.
 */
const PaymentsPanel: React.FC<Props> = ({
  hoaId,
  canRecord,
  canRefund,
  canReturn,
}) => {
  const [payments, setPayments] = useState<PaymentReturn[]>([]);
  const [unmatched, setUnmatched] = useState<PaymentReturn[]>([]);
  const [homes, setHomes] = useState<HomeReturn[]>([]);
  const [policy, setPolicy] = useState<BillingPolicyReturn | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<string>('');
  const [method, setMethod] = useState<string>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [recording, setRecording] = useState(false);
  const [matching, setMatching] = useState<PaymentReturn | null>(null);
  const [returning, setReturning] = useState<PaymentReturn | null>(null);
  const [refunding, setRefunding] = useState<PaymentReturn | null>(null);
  const [assessing, setAssessing] = useState<PaymentReturn | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [list, queue, homeList, pol] = await Promise.all([
        billingService.listHoaPayments({
          hoaId,
          page,
          perPage: PER_PAGE,
          status: (status || undefined) as PaymentStatus | undefined,
          method: (method || undefined) as PaymentMethod | undefined,
          from: from || undefined,
          to: to || undefined,
        }),
        billingService.listUnmatchedPayments({ hoaId, perPage: 100 }),
        homeService.getHomesByHoa({ hoaId }),
        billingService.getHoaPolicy({ hoaId }),
      ]);
      setPayments(list.data.payments);
      setTotal(list.data.total);
      setUnmatched(queue.data.payments);
      setHomes(homeList.data);
      setPolicy(pol.data);
    } catch (err) {
      console.error('Error loading payments:', err);
      setError(billingErrorMessage(err, "Couldn't load payments."));
    } finally {
      setLoading(false);
    }
  }, [hoaId, page, status, method, from, to]);

  useEffect(() => {
    void load();
  }, [load]);

  const allows = policy?.allow_credit_balances ?? false;
  const lastPage = Math.max(1, Math.ceil(total / PER_PAGE));
  const heldInSuspense = sumMoney(unmatched.map((p) => p.amount_unapplied));

  if (loading && payments.length === 0 && unmatched.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <>
      {error && (
        <div className="mb-4">
          <ShowAlert
            alertType="error"
            alertTitle="Couldn't load payments"
            alertMessage={error}
            isVisible={!!error}
            onClose={() => setError(null)}
            timeout={10000}
            isInline
          />
        </div>
      )}

      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <Subheading>Payments</Subheading>
          <Text className="mt-1">
            Everything received for this association, and anything still waiting
            on a decision.
          </Text>
        </div>
        {canRecord && (
          <Button color="sky" onClick={() => setRecording(true)}>
            Record a payment
          </Button>
        )}
      </div>

      {unmatched.length > 0 && (
        <div className="mb-8">
          <div className="rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 p-4">
            <Subheading>
              {unmatched.length} payment
              {unmatched.length === 1 ? '' : 's'} in suspense ·{' '}
              {formatMoney(heldInSuspense)}
            </Subheading>
            <Text className="mt-1">
              These arrived on an account number that could not be resolved —
              retired, mistyped, or from another system. None of it has been
              applied to anybody. Matching a payment to a home applies it
              immediately.
            </Text>
          </div>
          <div className="mt-4 space-y-4">
            {unmatched.map((p) => (
              <PaymentCard
                key={p.id}
                payment={p}
                allowsCreditBalances={allows}
                onMatch={canRecord ? setMatching : undefined}
              />
            ))}
          </div>
          <Divider className="my-8" />
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-1">
            Status
          </label>
          <FieldPicker
            label="Status"
            value={status || ANY}
            onChange={(v) => {
              setPage(1);
              setStatus(v === ANY ? '' : v);
            }}
            className="w-48"
            options={[
              { value: ANY, label: 'Any status' },
              ...paymentStatus.options().map((o) => ({
                value: o.value,
                label: o.label,
              })),
            ]}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-1">
            Method
          </label>
          <FieldPicker
            label="Method"
            value={method || ANY}
            onChange={(v) => {
              setPage(1);
              setMethod(v === ANY ? '' : v);
            }}
            className="w-44"
            options={[
              { value: ANY, label: 'Any method' },
              ...paymentMethod.options().map((o) => ({
                value: o.value,
                label: o.label,
              })),
            ]}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-1">
            Received from
          </label>
          <Input
            type="date"
            value={from}
            onChange={(e) => {
              setPage(1);
              setFrom(e.target.value);
            }}
            className="w-40"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-1">
            To
          </label>
          <Input
            type="date"
            value={to}
            onChange={(e) => {
              setPage(1);
              setTo(e.target.value);
            }}
            className="w-40"
          />
        </div>
        {(status || method || from || to) && (
          <Button
            plain
            onClick={() => {
              setStatus('');
              setMethod('');
              setFrom('');
              setTo('');
              setPage(1);
            }}
          >
            Clear filters
          </Button>
        )}
      </div>

      {payments.length === 0 ? (
        <div className="rounded-lg border border-dashed border-zinc-300 dark:border-zinc-700 p-6">
          <Text>
            {status || method || from || to
              ? 'No payments match these filters.'
              : 'Nothing has been received for this association yet.'}
          </Text>
        </div>
      ) : (
        <>
          <div className="space-y-4">
            {payments.map((p) => (
              <PaymentCard
                key={p.id}
                payment={p}
                allowsCreditBalances={allows}
                onMatch={canRecord ? setMatching : undefined}
                onReturn={canReturn ? setReturning : undefined}
                onRefund={canRefund ? setRefunding : undefined}
                onAssessFee={canReturn ? setAssessing : undefined}
              />
            ))}
          </div>

          <div className="mt-4 flex items-center justify-between">
            <Text>
              {total} payment{total === 1 ? '' : 's'} · page {page} of{' '}
              {lastPage}
            </Text>
            <div className="flex gap-2">
              <Button
                plain
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </Button>
              <Button
                plain
                disabled={page >= lastPage}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}

      <RecordPaymentDialog
        isOpen={recording}
        onClose={() => setRecording(false)}
        onRecorded={load}
        hoaId={hoaId}
        homes={homes}
      />
      <MatchPaymentDialog
        payment={matching}
        homes={homes}
        onClose={() => setMatching(null)}
        onDone={load}
      />
      <ReturnPaymentDialog
        payment={returning}
        nsfFee={policy?.nsf_fee}
        onClose={() => setReturning(null)}
        onDone={load}
      />
      <RefundPaymentDialog
        payment={refunding}
        onClose={() => setRefunding(null)}
        onDone={load}
      />
      <AssessFeeDialog
        payment={assessing}
        nsfFee={policy?.nsf_fee}
        onClose={() => setAssessing(null)}
        onDone={load}
      />
    </>
  );
};

export default PaymentsPanel;
